<template>
  <svg
    width="18"
    height="18"
    stroke-linecap="round"
    stroke-linejoin="round"
    class="fill-symbol"
  >
    <!-- Single fill: show a full circle -->
    <circle
      v-if="validFills.length === 1"
      cx="9"
      cy="9"
      r="8"
      :fill="validFills[0]"
      :stroke="getDarkerColor(validFills[0])"
      stroke-width="1"
    />
    
    <!-- Multiple fills: show pie chart sections -->
    <g v-else-if="validFills.length > 1">
      <path
        v-for="(fillColor, index) in validFills"
        :key="index"
        :d="getPathData(index, validFills.length)"
        :fill="fillColor"
        :stroke="getDarkerColor(fillColor)"
        stroke-width="1"
      />
    </g>
  </svg>
</template>

<script setup lang="ts">
import { contrastLightness, darken, lighten, toHSLA } from '@/helpers/utility/Color';
import { computed } from 'vue';

const props = defineProps<{
  fills: (string | null | undefined)[];
}>();

// Process fills, converting null to CSS variable for auto fills
const processedFills = computed(() => {
  return props.fills.map(fill => fill === null ? 'var(--color-background-deep)' : fill);
});

// Filter out undefined fills (but keep null values as they represent auto)
const validFills = computed(() => {
  return processedFills.value.filter((fill): fill is string => fill !== undefined);
});

// Function to get a darker color for stroke
const getDarkerColor = (color: string) => {
  // If it's a CSS variable, use a contrasting stroke color
  if (color.startsWith('var(--color-background-deep)')) {
    return 'color-mix(in oklch, var(--color-background-deep), rgb(var(--tint-contrast)))';
  }

  const lightness = contrastLightness.value;
  
  const fillHSLA = toHSLA(color);
  const fullOpacityColor = `hsla(${fillHSLA.h}, ${fillHSLA.s * 100}%, ${fillHSLA.l * 100}, 1)`;
  if (lightness < 50) {
    return darken(fullOpacityColor, 20);
  } else {
    return lighten(fullOpacityColor, 30);
  }
};

// Function to generate SVG path data for pie chart sections
const getPathData = (index: number, totalSections: number) => {
  const centerX = 9;
  const centerY = 9;
  const radius = 8;
  
  // Calculate angles for this section
  const anglePerSection = (2 * Math.PI) / totalSections;
  const startAngle = index * anglePerSection - Math.PI / 2; // Start from top
  const endAngle = (index + 1) * anglePerSection - Math.PI / 2;
  
  // Calculate start and end points
  const startX = centerX + radius * Math.cos(startAngle);
  const startY = centerY + radius * Math.sin(startAngle);
  const endX = centerX + radius * Math.cos(endAngle);
  const endY = centerY + radius * Math.sin(endAngle);
  
  // Determine if this is a large arc (greater than 180 degrees)
  const largeArcFlag = anglePerSection > Math.PI ? 1 : 0;
  
  // Create the path data
  return [
    `M ${centerX} ${centerY}`, // Move to center
    `L ${startX} ${startY}`,   // Line to start point
    `A ${radius} ${radius} 0 ${largeArcFlag} 1 ${endX} ${endY}`, // Arc to end point
    'Z' // Close path back to center
  ].join(' ');
};
</script>

<style lang="scss">
.fill-symbol {
  display: block;
  flex-shrink: 0;
}
</style>